"use client";

import { useGetNewsList } from "@/services/news/getNewsList";
import { NewsType, NewsTypeType } from "@/types/News";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Swiper, SwiperSlide } from "swiper/react";
import { renderClassName } from "@/utils/renderClassName";
import { Link } from "@/navigation";
import Image from "next/image";
import { Autoplay, EffectCoverflow, Pagination } from "swiper/modules";
import {
  MediaDetailsModal,
  MediaDetailsNewsModal,
  SliderControlButton,
  SubscriptionHardCodedModal,
} from "../SliderCards";
import Button from "@/components/Button";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { ProductType } from "@/types/Products";
import { groupProductsByCategory } from "@/utils/groupProductsByCategory";
import { Swiper as SwiperCore } from "swiper";
import useWindowResize from "@/hooks/useWindowRezise";
const DynamicModal = dynamic(() => import("@/components/Modal"), {
  ssr: false,
});

dayjs.extend(utc);
dayjs.extend(timezone);

type CategoryType = {
  title: string;
  href: string;
  image: string;
};

type ContentType = {
  categories: CategoryType[];
};

type MediaBannerProps = {
  section: {
    type: string;
    content: ContentType;
  };
  products?: ProductType[];
};

const MediaBanner = ({ section, products }: MediaBannerProps) => {
  const { data: banners } = useGetNewsList({
    type: NewsTypeType.MediaBanner,
    start_time_to: dayjs().tz("Europe/Moscow").format("YYYY-MM-DD"),
    end_time_from: dayjs().tz("Europe/Moscow").format("YYYY-MM-DD"),
  });
  const groupedByCategory = groupProductsByCategory(products || []);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [calculatedSlides, setCalculatedSlides] = useState(1);
  const [swiperWidth, setSwiperWidth] = useState<string | null>(null);
  const { windowWidth } = useWindowResize();

  useEffect(() => {
    const adjustWidth = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const totalSlides = groupedByCategory?.length || 0;
      const totalGaps = (totalSlides - 1) * 16;
      const totalSlideWidth =
        totalSlides * (windowWidth > 1500 ? 209 : 150) + totalGaps;

      if (totalSlideWidth < containerWidth) {
        setSwiperWidth(`${totalSlideWidth}px`);
      } else {
        setSwiperWidth(null);
      }
    };
    const calculateSlidesPerView = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const estimatedSlideWidth = windowWidth > 1500 ? 209 : 150 + 16; // e.g., slide width + spaceBetween

      const maxVisible = Math.floor(containerWidth / estimatedSlideWidth);
      setCalculatedSlides(Math.min(maxVisible, groupedByCategory?.length!));
      adjustWidth();
    };

    calculateSlidesPerView();

    window.addEventListener("resize", calculateSlidesPerView);
    window.addEventListener("resize", adjustWidth);
    return () => {
      window.removeEventListener("resize", calculateSlidesPerView);
      window.removeEventListener("resize", adjustWidth);
    };
  }, [groupedByCategory?.length, windowWidth]);

  const handleSlideChange = (swiper: SwiperCore) => {
    setShowPrev(!swiper.isBeginning);
    setShowNext(!swiper.isEnd);
  };

  return (
    <div className="media-banner">
      <Swiper
        className="banner-swiper"
        spaceBetween={26}
        slidesPerView={1}
        effect={"coverflow"}
        allowTouchMove={false}
        centeredSlides={true}
        modules={[EffectCoverflow, Autoplay, Pagination]}
        speed={700}
        autoplay={{
          delay: 8000,
        }}
        pagination={{
          type: "bullets",
          clickable: true,
        }}
        coverflowEffect={{
          rotate: 50,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: false,
        }}
        loop={true}
      >
        {banners && banners.length > 1 ? (
          <SliderControlButton showNext showPrev />
        ) : null}
        {banners?.map((card, index) => (
          <SwiperSlide key={`${card.title}-${index}`}>
            {({ isActive }) => <BannerItem isActive={isActive} card={card} />}
          </SwiperSlide>
        ))}
      </Swiper>
      <div ref={containerRef}>
        {groupedByCategory?.length! > 0 ? (
          <Swiper
            key={groupedByCategory?.length}
            spaceBetween={16}
            style={{
              width: swiperWidth || "100%",
              margin: swiperWidth ? "0 auto" : undefined,
            }}
            onSlideChange={(swiper: SwiperCore) => handleSlideChange(swiper)}
            onInit={(swiper: SwiperCore) => handleSlideChange(swiper)}
            className={renderClassName(
              "media-banner__swiper-category",
              `${
                banners?.length === 0
                  ? "media-banner__swiper-category--no-translate"
                  : ""
              }`
            )}
            slidesPerView={calculatedSlides}
          >
            {groupedByCategory &&
            groupedByCategory.length > 1 &&
            calculatedSlides < groupedByCategory.length ? (
              <SliderControlButton showNext={showNext} showPrev={showPrev} />
            ) : null}
            {groupedByCategory?.map((item) => (
              <SwiperSlide key={item.category.title}>
                <Link
                  className="media-banner__category-button"
                  href={`/media-library/category/${item.category.id}`}
                >
                  <span>{item.category.title}</span>
                  <Image
                    className="media-banner__category-button-image"
                    alt={item.category.title || ""}
                    src={item.category.image!}
                    fill
                  />
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : null}
      </div>
    </div>
  );
};

type BannerItemProps = {
  isActive?: boolean;
  card: NewsType;
};

const BannerItem = ({ isActive, card }: BannerItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<"Details" | "Subscription" | undefined>();
  return (
    <div className="banner__holder">
      <div className="banner__tag">
        <Image
          src="/assets/images/Lock.png"
          alt="Locked"
          width={24}
          height={24}
        />
      </div>
      <div
        className="banner__image"
        style={{ backgroundImage: `url(${card?.image})` }}
      ></div>
      <div
        className={`banner__content ${
          isActive ? "banner__content--active" : ""
        }`}
      >
        <h2 className="banner__title">{card.title}</h2>
        <p className="banner__text">{card.description}</p>
        <div className="banner__buttons-group">
          <Button
            onClick={() => {
              setIsOpen(true);
              setType("Subscription");
            }}
            type="primary"
          >
            Подписка
          </Button>
          <Button
            onClick={() => {
              setIsOpen(true);
              setType("Details");
            }}
            type="secondary"
          >
            Детали
          </Button>
        </div>
      </div>
      {isOpen && type ? (
        <DynamicModal
          isDarkerFull
          handleClose={() => {
            setIsOpen(false);
            document.body.classList.remove("no-scroll");
          }}
        >
          {type === "Subscription" ? (
            <SubscriptionHardCodedModal
              setIsModalOpen={(value) => {
                setIsOpen(value);
              }}
            />
          ) : null}
          {type === "Details" ? (
            <MediaDetailsNewsModal
              setIsModalOpen={(value) => {
                setIsOpen(value);
                setType(undefined);
              }}
              card={card}
            />
          ) : null}
        </DynamicModal>
      ) : null}
    </div>
  );
};

export default MediaBanner;
